import { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { NavigationBar } from '@client/components/layout';
import { PhotoCard } from '@client/components/features/feed';
import { LoadingSpinner } from '@client/components/common';
import { getPhotos, addReaction } from '@client/lib/api';
import { hapticSelection } from '@client/lib/haptics';
import { cn, getTimestampMs } from '@client/lib/utils';
import type { PhotoWithUser, PhotoLikedEvent } from '@shared/types';

type SortOption = 'newest' | 'likes';
const FEED_PAGE_SIZE = 10;

interface FeedPageProps {
  onNewPhoto?: (callback: (photo: PhotoWithUser) => void) => void;
  onPhotoLiked?: (callback: (event: PhotoLikedEvent['payload']) => void) => void;
  currentUserId?: string | null;
  onlineUsers?: number;
}

export function FeedPage({ onNewPhoto, onPhotoLiked, currentUserId, onlineUsers = 0 }: FeedPageProps) {
  const [photos, setPhotos] = useState<PhotoWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [reactionAnimation, setReactionAnimation] = useState<{ photoId: string; username: string; emoji: string } | null>(null);
  const loaderRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Store positions for FLIP animation
  const positionsRef = useRef<Map<string, DOMRect>>(new Map());
  const shouldAnimateRef = useRef(false);

  const fetchPhotos = useCallback(async (cursor?: string, sort: SortOption = sortBy) => {
    if (cursor) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const result = await getPhotos(cursor, FEED_PAGE_SIZE, sort);
      
      if (cursor) {
        setPhotos(prev => [...prev, ...result.photos]);
      } else {
        setPhotos(result.photos);
      }
      
      setNextCursor(result.nextCursor);
      setHasMore(result.nextCursor !== null);
    } catch (error) {
      console.error('Error fetching photos:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [sortBy]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  // Handle sort change
  const handleSortChange = (newSort: SortOption) => {
    if (newSort === sortBy) return;
    hapticSelection();
    setSortBy(newSort);
    setPhotos([]);
    setNextCursor(null);
    setHasMore(true);
    fetchPhotos(undefined, newSort);
  };

  // Capture positions before update (FLIP - First)
  const capturePositions = useCallback(() => {
    if (!containerRef.current) return;
    
    const newPositions = new Map<string, DOMRect>();
    const items = containerRef.current.querySelectorAll('[data-photo-id]');
    
    items.forEach((item) => {
      const id = item.getAttribute('data-photo-id');
      if (id) {
        newPositions.set(id, item.getBoundingClientRect());
      }
    });
    
    positionsRef.current = newPositions;
  }, []);

  // Handle new photos from WebSocket
  useEffect(() => {
    if (onNewPhoto) {
      const handleNewPhoto = (photo: PhotoWithUser) => {
        if (sortBy === 'likes') {
          capturePositions();
          shouldAnimateRef.current = true;
        }

        setPhotos(prev => {
          const withoutDuplicate = prev.filter(p => p.id !== photo.id);
          const updated = [photo, ...withoutDuplicate];

          if (sortBy === 'likes') {
            return [...updated].sort((a, b) => {
              if (b.likeCount !== a.likeCount) {
                return b.likeCount - a.likeCount;
              }
              return getTimestampMs(b.createdAt) - getTimestampMs(a.createdAt);
            });
          }

          return updated;
        });
      };
      onNewPhoto(handleNewPhoto);
    }
  }, [onNewPhoto, sortBy, capturePositions]);

  // Handle photo reaction events from WebSocket
  useEffect(() => {
    if (onPhotoLiked) {
      const handlePhotoLiked = (event: PhotoLikedEvent['payload']) => {
        // Capture positions before updating (for FLIP animation)
        if (sortBy === 'likes') {
          capturePositions();
          shouldAnimateRef.current = true;
        }

        // Update the like count, reactions and re-sort if needed
        setPhotos(prev => {
          const updated = prev.map(p =>
            p.id === event.photoId
              ? { 
                  ...p, 
                  likeCount: event.likeCount,
                  reactions: event.reactions || p.reactions,
                }
              : p
          );
          
          // Re-sort if we're in "likes" mode
          if (sortBy === 'likes') {
            return [...updated].sort((a, b) => {
              // Sort by likes desc, then by date desc
              if (b.likeCount !== a.likeCount) {
                return b.likeCount - a.likeCount;
              }
              return getTimestampMs(b.createdAt) - getTimestampMs(a.createdAt);
            });
          }
          
          return updated;
        });

        // Show animation only for reactions (not removals) and if it's not the current user
        if (event.liked && event.likedByUserId !== currentUserId) {
          setReactionAnimation({ 
            photoId: event.photoId, 
            username: event.likedByUsername,
            emoji: event.emoji || '❤️'
          });
          setTimeout(() => setReactionAnimation(null), 2500);
        }
      };
      onPhotoLiked(handlePhotoLiked);
    }
  }, [onPhotoLiked, currentUserId, sortBy, capturePositions]);

  // FLIP animation - animate after DOM updates
  useLayoutEffect(() => {
    if (!shouldAnimateRef.current || !containerRef.current) return;
    shouldAnimateRef.current = false;

    const items = containerRef.current.querySelectorAll('[data-photo-id]');
    
    items.forEach((item) => {
      const id = item.getAttribute('data-photo-id');
      if (!id) return;
      
      const firstRect = positionsRef.current.get(id);
      if (!firstRect) return;
      
      const lastRect = item.getBoundingClientRect();
      const deltaY = firstRect.top - lastRect.top;
      
      // Only animate if position changed
      if (Math.abs(deltaY) > 1) {
        const element = item as HTMLElement;
        
        // Invert - move to old position
        element.style.transform = `translateY(${deltaY}px)`;
        element.style.transition = 'none';
        
        // Force reflow
        element.offsetHeight;
        
        // Play - animate to new position
        element.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.1, 0.25, 1)';
        element.style.transform = '';
        
        // Clean up after animation
        const cleanup = () => {
          element.style.transition = '';
          element.removeEventListener('transitionend', cleanup);
        };
        element.addEventListener('transitionend', cleanup);
      }
    });
  }, [photos]);

  // Handle emoji reaction
  const handleReact = async (photoId: string, emoji: string) => {
    // Capture positions before updating (for FLIP animation)
    if (sortBy === 'likes') {
      capturePositions();
      shouldAnimateRef.current = true;
    }

    try {
      const result = await addReaction(photoId, emoji);
      
      setPhotos(prev => {
        const updated = prev.map(p =>
          p.id === photoId
            ? { 
                ...p, 
                isLikedByCurrentUser: result.userReactions.length > 0, 
                currentUserReactions: result.userReactions,
                likeCount: result.totalCount,
                reactions: result.reactions,
              }
            : p
        );
        
        // Re-sort if we're in "likes" mode
        if (sortBy === 'likes') {
          return [...updated].sort((a, b) => {
            if (b.likeCount !== a.likeCount) {
              return b.likeCount - a.likeCount;
            }
            return getTimestampMs(b.createdAt) - getTimestampMs(a.createdAt);
          });
        }
        
        return updated;
      });
    } catch (error) {
      console.error('Error adding reaction:', error);
      throw error;
    }
  };

  // Infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && nextCursor) {
          fetchPhotos(nextCursor);
        }
      },
      { threshold: 0.1 }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loadingMore, nextCursor, fetchPhotos]);

  // Sort toggle component
  const OnlineCounter = () => (
    <div className="flex items-center gap-1.5 text-[13px] leading-[18px] font-medium text-muted-foreground">
      <span className="h-2 w-2 rounded-full bg-green-500" />
      <span>{onlineUsers}</span>
    </div>
  );

  const SortToggle = () => (
    <div className="flex bg-secondary rounded-lg p-0.5">
      <button
        onClick={() => handleSortChange('newest')}
        className={cn(
          'px-3 py-1 text-[13px] font-medium rounded-md transition-colors',
          sortBy === 'newest' 
            ? 'bg-background text-foreground shadow-sm' 
            : 'text-muted-foreground'
        )}
      >
        Neu
      </button>
      <button
        onClick={() => handleSortChange('likes')}
        className={cn(
          'px-3 py-1 text-[13px] font-medium rounded-md transition-colors',
          sortBy === 'likes' 
            ? 'bg-background text-foreground shadow-sm' 
            : 'text-muted-foreground'
        )}
      >
        Beliebt
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="bg-background">
        <NavigationBar title="Galerie" leftAction={<OnlineCounter />} rightAction={<SortToggle />} />
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className="bg-background">
        <NavigationBar title="Galerie" leftAction={<OnlineCounter />} rightAction={<SortToggle />} />
        <div className="flex flex-col items-center justify-center py-20 px-6">
          <div className="text-6xl mb-4">📷</div>
          <h2 className="text-[22px] leading-[28px] font-bold text-center mb-2">
            Noch keine Fotos
          </h2>
          <p className="text-[17px] leading-[22px] text-muted-foreground text-center">
            Starte mit deiner ersten Foto-Mission und fülle die Galerie für Sabrina & Stefan.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background">
      <NavigationBar title="Galerie" leftAction={<OnlineCounter />} rightAction={<SortToggle />} />
      
      <div className="max-w-lg mx-auto">
        <div ref={containerRef} className="divide-y divide-[var(--color-ios-separator)]">
          {photos.map((photo, index) => (
            <div key={photo.id} data-photo-id={photo.id}>
              <PhotoCard
                photo={photo}
                onReact={handleReact}
                priority={index < 2}
                externalLike={
                  reactionAnimation?.photoId === photo.id 
                    ? { username: reactionAnimation.username, emoji: reactionAnimation.emoji }
                    : undefined
                }
              />
            </div>
          ))}
        </div>

        {/* Infinite scroll loader */}
        <div ref={loaderRef} className="py-8 flex justify-center">
          {loadingMore && <LoadingSpinner />}
          {!hasMore && photos.length > 0 && (
            <p className="text-[13px] leading-[18px] text-muted-foreground">
              Keine weiteren Fotos
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
